// src/admin/pages/AdminLoginPage.jsx
import { useNavigate } from "react-router-dom";
import { authService } from "../services/authService";
import { useState, useEffect } from "react";
import { useTheme } from "../../context/ThemeContext";
import { FaEye, FaEyeSlash } from "react-icons/fa";

export default function AdminLoginPage() {
  const { isDark } = useTheme();
  const [formData, setFormData] = useState({
    username: "",
    password: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showAlert, setShowAlert] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (authService.isAuthenticated()) {
      navigate("/admin/dashboard");
    }
  }, [navigate]);

  const handleChange = (event) => {
    setFormData((prev) => ({
      ...prev,
      [event.target.name]: event.target.value,
    }));
    // Clear error when user types
    if (error) {
      setError("");
      setShowAlert(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError("");
    setShowAlert(false);

    try {
      await authService.login({
        username: formData.username,
        password: formData.password,
      });
      navigate("/admin/dashboard");
    } catch (error) {
      console.error(error);
      const errorMessage =
        error?.response?.data?.detail || "Invalid credentials. Please try again.";
      setError(errorMessage);
      setShowAlert(true);
    } finally {
      setLoading(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <div
      className={`
        relative min-h-screen overflow-hidden
        flex items-center justify-center px-6
        ${isDark ? "bg-black text-white" : "bg-gray-50 text-gray-900"}
      `}
    >
      {/* Background Effects */}
      {isDark && (
        <>
          <div className="absolute top-[-200px] left-[-100px] w-[500px] h-[500px] bg-blue-800/15 rounded-full blur-3xl" />
          <div className="absolute bottom-[-250px] right-[-150px] w-[600px] h-[600px] bg-blue-600/15 rounded-full blur-3xl" />
        </>
      )}

      {/* Grid Pattern */}
      <div
        className={`absolute inset-0 ${isDark ? "opacity-[0.03]" : "opacity-[0.02]"}`}
        style={{
          backgroundImage: `
            linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px)
          `,
          backgroundSize: "70px 70px",
        }}
      />

      <div className="relative z-10 w-full max-w-7xl grid lg:grid-cols-2 gap-12 items-center">
        {/* Left Branding Section */}
        <div className="hidden lg:block">
          <span className="inline-flex items-center px-4 py-2 rounded-full border border-blue-800/30 bg-blue-800/10 text-blue-800 text-xs tracking-[0.2em] uppercase font-medium">
            Dorots CMS
          </span>

          <h1 className="mt-8 text-6xl font-bold leading-[1.1]">
            Manage
            <br />
            Content &
            <br />
            Analytics
          </h1>

          <p className={`mt-8 max-w-xl text-lg leading-relaxed ${isDark ? "text-gray-300" : "text-gray-600"}`}>
            Publish blog posts, track engagement,
            monitor campaign performance and manage
            the Dorots technology community from a
            single administration platform.
          </p>

          <div className="mt-10 space-y-5">
            <div className="flex items-center gap-3">
              <span className="text-blue-800">✓</span>
              <span>Blog Publishing</span>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-blue-800">✓</span>
              <span>Analytics & Reporting</span>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-blue-800">✓</span>
              <span>Advertisement Tracking</span>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-blue-800">✓</span>
              <span>Content Management</span>
            </div>
          </div>
        </div>

        {/* Login Card */}
        <div
          className={`
            rounded-3xl border p-8 md:p-10 backdrop-blur-xl
            ${isDark ? "bg-white/5 border-white/10" : "bg-white border-gray-200 shadow-xl"}
          `}
        >
          <div className="mb-8">
            <h2 className="text-3xl font-bold">Welcome Back</h2>
            <p className={`mt-2 ${isDark ? "text-gray-400" : "text-gray-600"}`}>
              Sign in to continue to Dorots CMS
            </p>
          </div>

          {/* Error Alert */}
          {showAlert && error && (
            <div className="mb-6 p-4 rounded-xl bg-red-500/10 border border-red-500/30 text-red-500 flex items-start gap-3">
              <span className="text-lg mt-0.5">⚠️</span>
              <div>
                <p className="font-semibold text-sm">Login Failed</p>
                <p className="text-sm opacity-90">{error}</p>
              </div>
              <button
                onClick={() => {
                  setShowAlert(false);
                  setError("");
                }}
                className="ml-auto text-red-500 hover:text-red-600 transition-colors"
                aria-label="Close alert"
              >
                ✕
              </button>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Username */}
            <div>
              <label className="block mb-2 font-medium">Username</label>
              <input
                type="text"
                name="username"
                value={formData.username}
                onChange={handleChange}
                required
                className={`
                  w-full px-4 py-3 rounded-xl border outline-none transition-all
                  ${isDark
                    ? "bg-white/5 border-white/10 text-white placeholder:text-gray-500 focus:border-blue-800"
                    : "bg-white border-gray-300 text-gray-900 focus:border-blue-800"
                  }
                `}
                placeholder="Enter username"
                autoComplete="username"
              />
            </div>

            {/* Password with Show/Hide */}
            <div>
              <label className="block mb-2 font-medium">Password</label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  className={`
                    w-full px-4 py-3 pr-12 rounded-xl border outline-none transition-all
                    ${isDark
                      ? "bg-white/5 border-white/10 text-white placeholder:text-gray-500 focus:border-blue-800"
                      : "bg-white border-gray-300 text-gray-900 focus:border-blue-800"
                    }
                  `}
                  placeholder="Enter password"
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={togglePasswordVisibility}
                  className={`
                    absolute right-3 top-1/2 -translate-y-1/2
                    p-2 rounded-lg transition-colors
                    ${isDark
                      ? "text-gray-400 hover:text-white"
                      : "text-gray-500 hover:text-gray-900"
                    }
                  `}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? (
                    <FaEyeSlash className="w-5 h-5" />
                  ) : (
                    <FaEye className="w-5 h-5" />
                  )}
                </button>
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className={`
                w-full px-6 py-4 rounded-xl
                bg-gradient-to-r from-blue-800 to-blue-600
                text-white font-semibold
                transition-all duration-300
                ${loading
                  ? "opacity-70 cursor-not-allowed"
                  : "hover:scale-[1.01] shadow-[0_0_40px_rgba(30,64,175,0.25)] hover:shadow-[0_0_60px_rgba(30,64,175,0.35)]"
                }
              `}
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Signing In...
                </span>
              ) : (
                "Sign In"
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}